import React, { useState, useEffect, useContext } from 'react';
import { toast } from 'react-toastify';
import { UserContext } from '../contexts/UserContext';
import { Edit2, Eye, Save, X, Info } from 'lucide-react';
import api from '../services/apiService';

const PPEEmailTemplateEditor = ({ onTemplateChange }) => {
  const { user } = useContext(UserContext);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [saving, setSaving] = useState(false);

  // Template type configurations
  const templateTypeConfig = {
    // AUTOMATIC EMAIL TEMPLATES (for cron jobs - management commands)
    auto_inspection_internal: {
      name: 'Auto Inspection Validity Reminder (Internal Users)',
      description: 'Automatic email sent to internal users about inspection expiry - used by cron jobs',
      icon: '🤖',
      category: 'Automatic Emails',
      sampleVariables: {
        recipient_name: 'John Smith',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        expired_equipment: '<div style="color: red;">⚠️ Hard hat (SN: 123) - Expired</div>',
        expiring_7_days: '<div>Safety boots (SN: 456) - Expires in 7 days</div>',
        expiring_30_days: '<div>Gloves (SN: 789) - Expires in 30 days</div>'
      }
    },
    auto_inspection_external: {
      name: 'Auto Inspection Validity Reminder (External Companies)',
      description: 'Automatic email sent to external companies about inspection expiry - used by cron jobs',
      icon: '🤖',
      category: 'Automatic Emails',
      sampleVariables: {
        recipient_name: 'Jane External',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        expired_equipment: '<div style="color: red;">⚠️ Hard hat (SN: 123) - Expired</div>',
        expiring_7_days: '<div>Safety boots (SN: 456) - Expires in 7 days</div>',
        expiring_30_days: '<div>Gloves (SN: 789) - Expires in 30 days</div>'
      }
    },
    auto_validity_internal: {
      name: 'Auto Validity Date Reminder (Internal Users)',
      description: 'Automatic email sent to internal users about manufacturer validity expiry - used by cron jobs',
      icon: '🤖',
      category: 'Automatic Emails',
      sampleVariables: {
        recipient_name: 'Mike Johnson',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        equipment_list: '<div>Safety harness (SN: ABC123) - Expires: 2025-09-15</div><div>Hard hat (SN: DEF456) - Expires: 2025-10-01</div>'
      }
    },
    auto_validity_external: {
      name: 'Auto Validity Date Reminder (External Companies)',
      description: 'Automatic email sent to external companies about manufacturer validity expiry - used by cron jobs',
      icon: '🤖',
      category: 'Automatic Emails',
      sampleVariables: {
        recipient_name: 'External Manager',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        equipment_list: '<div>Safety harness (SN: ABC123) - Expires: 2025-09-15</div><div>Hard hat (SN: DEF456) - Expires: 2025-10-01</div>'
      }
    },
    
    // MANUAL EMAIL TEMPLATES (for manual sending via InspectionNotification component)
    manual_inspection_internal: {
      name: 'Inspection Validity Reminder (Internal Users)',
      description: 'Manual email sent to internal users about inspection expiry - used via InspectionNotification component',
      icon: '✋',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'John Smith',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        expired_equipment: '<div style="color: red;">⚠️ Hard hat (SN: 123) - Expired</div>',
        expiring_7_days: '<div>Safety boots (SN: 456) - Expires in 7 days</div>',
        expiring_30_days: '<div>Gloves (SN: 789) - Expires in 30 days</div>'
      }
    },
    manual_inspection_external: {
      name: 'Inspection Validity Reminder (External Companies)',
      description: 'Manual email sent to external companies about inspection expiry - used via InspectionNotification component',
      icon: '✋',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'Jane External',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        expired_equipment: '<div style="color: red;">⚠️ Hard hat (SN: 123) - Expired</div>',
        expiring_7_days: '<div>Safety boots (SN: 456) - Expires in 7 days</div>',
        expiring_30_days: '<div>Gloves (SN: 789) - Expires in 30 days</div>'
      }
    },
    manual_inspection_with_inspectors: {
      name: 'Inspection Validity Reminder with Inspectors (Internal Only)',
      description: 'Manual email sent to internal users about inspection expiry with available inspectors - used via InspectionNotification component',
      icon: '👥',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'Jane Doe',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        expired_equipment: '<div style="color: red;">⚠️ Hard hat (SN: 123) - Expired</div>',
        expiring_7_days: '<div>Safety boots (SN: 456) - Expires in 7 days</div>',
        expiring_30_days: '<div>Gloves (SN: 789) - Expires in 30 days</div>',
        inspector_list: '<ul><li>John Inspector (john@company.com)</li><li>Jane Inspector (jane@company.com)</li></ul>'
      }
    },
    manual_validity_internal: {
      name: 'Validity Date Reminder (Internal Users)',
      description: 'Manual email sent to internal users about manufacturer validity expiry - used via InspectionNotification component',
      icon: '✋',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'Mike Johnson',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        equipment_list: '<div>Safety harness (SN: ABC123) - Expires: 2025-09-15</div><div>Hard hat (SN: DEF456) - Expires: 2025-10-01</div>'
      }
    },
    manual_validity_external: {
      name: 'Validity Date Reminder (External Companies)',
      description: 'Manual email sent to external companies about manufacturer validity expiry - used via InspectionNotification component',
      icon: '✋',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'External Manager',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        equipment_list: '<div>Safety harness (SN: ABC123) - Expires: 2025-09-15</div><div>Hard hat (SN: DEF456) - Expires: 2025-10-01</div>'
      }
    },
    inspector_notification: {
      name: 'Inspection Request Notification (To Inspectors)',
      description: 'Email sent to inspectors when they are selected for equipment inspection - used via InspectionNotification component',
      icon: '👨‍🔧',
      category: 'Manual Emails',
      sampleVariables: {
        recipient_name: 'John Inspector',
        company_name: 'Vulcan Industries',
        current_date: '2025-08-29',
        equipment_list: '<div>Hard hat (SN: HH-123) - Inspection needed</div><div>Safety harness (SN: SH-456) - Inspection needed</div>',
        requester_name: 'Mike Manager',
        requester_email: 'mike@company.com'
      }
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await api.get('/ppe-email-templates/available_templates/');
      setTemplates(response.data);
    } catch (error) {
      console.error('Failed to load PPE email templates:', error);
      toast.error('Failed to load PPE email templates');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (templateData) => {
    // Edit company template if it exists
    if (templateData.has_company_template && templateData.company_template) {
      setEditingTemplate({
        id: templateData.company_template.id,
        name: templateData.company_template.name,
        template_type: templateData.template_type,
        subject: templateData.company_template.subject,
        html_content: templateData.company_template.html_content,
        text_content: templateData.company_template.text_content || '',
        is_active: templateData.company_template.is_active
      });
    } else {
      // Show system template for preview (read-only)
      setPreviewTemplate({
        name: templateData.system_template.name,
        template_type: templateData.template_type,
        subject: templateData.system_template.subject,
        html_content: templateData.system_template.html_content,
        text_content: templateData.system_template.text_content || '',
        is_system: true
      });
      setShowPreview(true);
    }
  };

  const handleCopyTemplate = async (templateData) => {
    if (!templateData.can_customize || templateData.has_company_template) {
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/ppe-email-templates/copy_system_template/', {
        template_type: templateData.template_type
      });
      
      toast.success(`Copied system template: ${templateData.display_name}`);
      await loadTemplates(); // Reload to show the new company template
    } catch (error) {
      console.error('Failed to copy template:', error);
      const message = error.response?.data?.error || 'Failed to copy template';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = async (templateData) => {
    if (!templateData.has_company_template) {
      return;
    }

    if (!window.confirm(`Are you sure you want to reset "${templateData.display_name}" to system default? This will delete your custom template.`)) {
      return;
    }

    try {
      setSaving(true);
      await api.delete(`/ppe-email-templates/${templateData.company_template.id}/`);
      
      toast.success(`Reset template to default: ${templateData.display_name}`);
      await loadTemplates(); // Reload to show updated data
    } catch (error) {
      console.error('Failed to reset template:', error);
      const message = error.response?.data?.error || 'Failed to reset template to default';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleSave = async () => {
    if (!editingTemplate) return;

    try {
      setSaving(true);
      
      // Send template_type as it's required by serializer, but it remains immutable
      const payload = {
        template_type: editingTemplate.template_type, // Required by serializer but won't be changed
        name: editingTemplate.name,
        subject: editingTemplate.subject,
        html_content: editingTemplate.html_content,
        text_content: editingTemplate.text_content,
        is_active: editingTemplate.is_active
      };

      const response = await api.put(`/ppe-email-templates/${editingTemplate.id}/`, payload);
      
      setEditingTemplate(null);
      toast.success('Template updated successfully');
      await loadTemplates(); // Reload templates to get updated data structure
      
      if (onTemplateChange) {
        onTemplateChange();
      }
    } catch (error) {
      console.error('Failed to save template:', error);
      const errorMessage = error.response?.data?.template_type?.[0] || 
                           error.response?.data?.message || 
                           error.response?.data?.error || 
                           'Failed to save template';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = (templateData) => {
    const config = templateTypeConfig[templateData.template_type];
    if (!config) return;

    // Get the active template (company or system)
    const activeTemplate = templateData.active_template_source === 'company' 
      ? templateData.company_template 
      : templateData.system_template;

    if (!activeTemplate) return;

    // Replace template variables with sample data for preview
    let previewHtml = activeTemplate.html_content;
    let previewSubject = activeTemplate.subject;

    Object.entries(config.sampleVariables).forEach(([key, value]) => {
      const regex = new RegExp(`{{\\s*${key}\\s*}}`, 'g');
      previewHtml = previewHtml.replace(regex, value);
      previewSubject = previewSubject.replace(regex, value);
    });

    setPreviewTemplate({
      name: activeTemplate.name,
      template_type: templateData.template_type,
      subject: previewSubject,
      html_content: previewHtml,
      text_content: activeTemplate.text_content,
      is_system: templateData.active_template_source === 'system_default',
      source_info: templateData.active_template_source === 'company' ? 'Custom Company Template' : 'System Default Template'
    });
    setShowPreview(true);
  };

  const insertVariable = (variable) => {
    if (!editingTemplate) return;

    const textarea = document.getElementById('html-content-editor');
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = textarea.value;
    const before = text.substring(0, start);
    const after = text.substring(end, text.length);
    const newText = before + `{{${variable}}}` + after;

    setEditingTemplate({
      ...editingTemplate,
      html_content: newText
    });

    // Set cursor position after the inserted variable
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + variable.length + 4, start + variable.length + 4);
    }, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  // Filter templates based on user permissions
  const filteredTemplates = templates.filter(templateData => {
    // Check if template is for external companies
    const isExternalTemplate = templateData.template_type.includes('_external');
    
    // If it's an external template, only show if user's company can do inspections
    if (isExternalTemplate) {
      return user?.company_can_inspection === true;
    }
    
    return true; // Show all internal templates
  });

  // Group filtered templates by category  
  const groupedTemplates = filteredTemplates.reduce((groups, templateData) => {
    const config = templateTypeConfig[templateData.template_type];
    const category = config?.category || 'Other';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(templateData);
    return groups;
  }, {});

  return (
    <div className="space-y-8">
      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
              <span className="text-blue-600 text-sm font-semibold">ℹ</span>
            </div>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">PPE Email Template System</h3>
            <div className="text-xs text-blue-700 mt-1">
              <strong>Automatic Emails</strong> 🤖 are sent by cron jobs (daily automated notifications)<br/>
              <strong>Manual Emails</strong> ✋ are sent via InspectionNotification component (user-triggered)
            </div>
          </div>
        </div>
      </div>

      {/* Templates List - Grouped by Category */}
      {/* Define category order: Automatic Emails, Manual Emails, Other */}
      {['Automatic Emails', 'Manual Emails', 'Other'].filter(category => groupedTemplates[category]).map((category) => {
        const categoryTemplates = groupedTemplates[category];
        return (
        <div key={category} className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 pb-2 border-b border-gray-200">
            {category === 'Automatic Emails' ? '🤖 ' : category === 'Manual Emails' ? '✋ ' : '📋 '}{category}
          </h3>
          <div className="grid gap-4">
            {categoryTemplates.map((templateData) => {
              const config = templateTypeConfig[templateData.template_type];
              const activeTemplate = templateData.active_template_source === 'company' 
                ? templateData.company_template 
                : templateData.system_template;
              
              return (
                <div key={templateData.template_type} className="bg-white border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">{config?.icon || '📧'}</span>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2">
                          <h4 className="text-lg font-medium text-gray-900">
                            {templateData.display_name}
                          </h4>
                          <div className="flex items-center space-x-1">
                            {templateData.is_using_default ? (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Using Default
                              </span>
                            ) : (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Customized
                              </span>
                            )}
                          </div>
                        </div>
                        <p className="text-sm text-gray-600">{config?.description}</p>
                        <div className="flex items-center space-x-4 mt-1">
                          <p className="text-xs text-gray-500">
                            Currently using: <span className="font-medium">
                              {templateData.active_template_source === 'company' ? 'Custom template' : 'System default'}
                            </span>
                          </p>
                          <p className="text-xs text-gray-500">
                            Status: <span className={activeTemplate?.is_active ? 'text-green-600' : 'text-red-600'}>
                              {activeTemplate?.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePreview(templateData)}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <Eye className="w-4 h-4 mr-1" />
                        Preview
                      </button>
                      
                      {/* Show Copy button if using system default and can customize */}
                      {templateData.is_using_default && templateData.can_customize ? (
                        <button
                          onClick={() => handleCopyTemplate(templateData)}
                          disabled={saving}
                          className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                        >
                          <Edit2 className="w-4 h-4 mr-1" />
                          {saving ? 'Copying...' : 'Customize'}
                        </button>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEdit(templateData)}
                            disabled={!templateData.has_company_template}
                            className="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                            title={!templateData.has_company_template ? 'Click "Customize" to create editable version' : 'Edit custom template'}
                          >
                            <Edit2 className="w-4 h-4 mr-1" />
                            Edit
                          </button>
                          
                          {/* Show Reset to Default button if has custom template */}
                          {templateData.has_company_template && (
                            <button
                              onClick={() => handleResetToDefault(templateData)}
                              disabled={saving}
                              className="inline-flex items-center px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50"
                              title="Reset to system default template"
                            >
                              <X className="w-4 h-4 mr-1" />
                              {saving ? 'Resetting...' : 'Reset Default'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        );
      })}

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <span className="text-4xl">📧</span>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No templates found</h3>
            <p className="mt-1 text-sm text-gray-500">
              Use the management command to create default templates.
            </p>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Edit Template: {templateTypeConfig[editingTemplate.template_type]?.name}
              </h3>
              <button
                onClick={() => setEditingTemplate(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-6">
              {/* Template Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Template Name
                </label>
                <input
                  type="text"
                  value={editingTemplate.name}
                  onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Email Subject */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Subject
                </label>
                <input
                  type="text"
                  value={editingTemplate.subject}
                  onChange={(e) => setEditingTemplate({...editingTemplate, subject: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Available Variables */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-900 mb-2 flex items-center">
                  <Info className="w-4 h-4 mr-2" />
                  Available Variables (click to insert)
                </h4>
                <div className="flex flex-wrap gap-2">
                  {Object.keys(templateTypeConfig[editingTemplate.template_type]?.sampleVariables || {}).map((variable) => (
                    <button
                      key={variable}
                      onClick={() => insertVariable(variable)}
                      className="px-2 py-1 text-xs font-mono bg-blue-100 text-blue-800 rounded hover:bg-blue-200"
                    >
                      {`{{${variable}}}`}
                    </button>
                  ))}
                </div>
              </div>

              {/* HTML Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  HTML Content
                </label>
                <textarea
                  id="html-content-editor"
                  value={editingTemplate.html_content}
                  onChange={(e) => setEditingTemplate({...editingTemplate, html_content: e.target.value})}
                  className="w-full h-64 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                  placeholder="Enter HTML content..."
                />
              </div>

              {/* Text Content */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plain Text Content (optional fallback)
                </label>
                <textarea
                  value={editingTemplate.text_content}
                  onChange={(e) => setEditingTemplate({...editingTemplate, text_content: e.target.value})}
                  className="w-full h-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter plain text content..."
                />
              </div>

              {/* Active Status */}
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editingTemplate.is_active}
                    onChange={(e) => setEditingTemplate({...editingTemplate, is_active: e.target.checked})}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="ml-2 text-sm text-gray-700">Template is active</span>
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end space-x-3 pt-4 border-t">
                <button
                  onClick={() => setEditingTemplate(null)}
                  disabled={saving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <Save className="w-4 h-4 mr-1" />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {showPreview && previewTemplate && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-8 mx-auto p-5 border w-11/12 max-w-4xl shadow-lg rounded-md bg-white">
            <div className="flex justify-between items-center mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Preview: {previewTemplate.name}
                </h3>
                {previewTemplate.source_info && (
                  <p className="text-sm text-gray-600 mt-1">
                    <span className={previewTemplate.is_system ? 'text-blue-600' : 'text-green-600'}>
                      {previewTemplate.source_info}
                    </span>
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowPreview(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Subject Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subject:</label>
                <div className="p-2 bg-gray-50 rounded border text-sm">
                  {previewTemplate.subject}
                </div>
              </div>

              {/* Email Content Preview */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email Content:</label>
                <div 
                  className="p-4 bg-white border rounded max-h-96 overflow-y-auto"
                  dangerouslySetInnerHTML={{ __html: previewTemplate.html_content }}
                />
              </div>
            </div>

            <div className="flex justify-end pt-4 border-t mt-4">
              <button
                onClick={() => setShowPreview(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PPEEmailTemplateEditor;